/**
 * Reconciliation math for the payment detail page.
 *
 * This is the browser copy of calcLinkedSummary in
 * server/utils/accountingHelpers.js. The payments list shows the status the
 * server stores, so the two copies must agree, or the list and the detail page
 * show different statuses for the same payment. paymentReconciliation.test.ts
 * runs both copies on the same fixtures.
 *
 * The input types below list only the fields the math reads. The payment
 * detail payload (GET /api/accounting/payments/:id) has many more fields and is
 * assignable to them.
 */

/** AIB net totals can drift vs bank credits after fee rounding. */
export const BRAINTREE_AMOUNT_TOLERANCE = 0.49;

export interface ReconciliationLinkedOrder {
  total: string;
  /** How much of this order is covered by this payment. Null = full order total. */
  partialAmount: string | null;
  processingFee: string | null;
  settlementAmount?: string | null;
  /** Item-level force close (SW-2489). Non-null = resolved by hand. */
  forceClosedAt?: string | null;
  creditNotes?: { amount: number }[];
}

export interface ReconciliationVascoLink {
  /** Claimed portion in payment currency; null = full invoice amount. */
  amount: string | null;
  /** Vasco invoice amount (EUR); null while the link is unverified. */
  invoiceAmount: string | null;
}

export interface ReconciliationPaypalSummary {
  refundsNet: number;
  totalNet: number;
}

export interface ReconciliationRevolutSummary {
  refundsNet: number;
  refundFundingNet: number;
  totalNet: number;
  unlinkedSettlements: {
    forceClosedAt: string | null;
    settlementAmount: number | null;
    revolutGross: number;
  }[];
}

export interface ReconciliationBraintreeSummary {
  refundsNet: number;
  feesNet?: number;
  totalNet: number;
  unlinkedSettlements: {
    forceClosedAt: string | null;
    net: number | null;
    braintreeGross: number;
  }[];
}

export interface ReconciliationPaymentInput {
  amount: number;
  paymentSource: string | null;
  linkedOrders: ReconciliationLinkedOrder[];
  vascoLinks?: ReconciliationVascoLink[] | null;
  paypalSummary: ReconciliationPaypalSummary | null;
  revolutSummary: ReconciliationRevolutSummary | null;
  braintreeSummary: ReconciliationBraintreeSummary | null;
  revolutCarryoverIn: number | null;
  revolutCarryoverOut: number | null;
  settlementFee: number;
}

export interface PaymentReconciliation {
  hasFeesData: boolean;
  isRevolut: boolean;
  isBraintree: boolean;
  isPaypal: boolean;
  linkedGross: number;
  linkedFees: number;
  linkedSettlementsNet: number;
  linkedNet: number;
  linkedSum: number;
  carryoverIn: number;
  carryoverOut: number;
  hasCarryover: boolean;
  refundFundingNet: number;
  settlementFee: number;
  vascoLinkedNet: number;
  forcedSettlementsNet: number;
  openUnlinkedNet: number;
  braintreeFeesNet: number;
  comparisonSumBeforeCredits: number;
  creditNotesNet: number;
  amountTolerance: number;
  creditApplied: boolean;
  comparisonSum: number;
  remaining: number;
  isExact: boolean;
  isOverpaid: boolean;
  isUnderpaid: boolean;
}

export function computePaymentReconciliation(payment: ReconciliationPaymentInput): PaymentReconciliation {
  const hasFeesData = payment.linkedOrders.some((o) => o.processingFee != null);
  const isRevolut = payment.paymentSource === 'Revolut Credit Card';
  const isBraintree = payment.paymentSource === 'Braintree';
  const isPaypal = payment.paymentSource === 'PayPal';
  // Force-closed items (SW-2489) are excluded from the reconciliation sums —
  // mirrors calcLinkedSummary's activeOrders on the server.
  const activeLinkedOrders = payment.linkedOrders.filter((o) => !o.forceClosedAt);
  // Match calcLinkedSummary on the server: treat missing/undefined the same
  // as null so test fixtures (and any pre-migration link rows) don't slip
  // into the settlement-net branch and yield NaN.
  const hasSettlementAmounts = activeLinkedOrders.some((o) => o.settlementAmount != null);

  // Use partial_amount when set (SW-2410); this mirrors calcLinkedSummary on the server.
  const linkedGross = activeLinkedOrders.reduce(
    (acc, o) => acc + (o.partialAmount != null ? parseFloat(o.partialAmount) : parseFloat(o.total)),
    0
  );
  const linkedFees  = activeLinkedOrders.reduce((acc, o) => acc + (o.processingFee != null ? parseFloat(o.processingFee) : 0), 0);
  // Revolut uses the authoritative settlement_amount column from payment_order_links
  // (what Revolut actually deposited). Other sources fall back to gross − fee math.
  const linkedSettlementsNet = (isRevolut || isBraintree) && hasSettlementAmounts
    ? activeLinkedOrders.reduce(
        (acc, o) => acc + (o.settlementAmount != null
          ? parseFloat(o.settlementAmount)
          : (o.partialAmount != null ? parseFloat(o.partialAmount) : parseFloat(o.total))
            - (o.processingFee ? parseFloat(o.processingFee) : 0)),
        0,
      )
    : linkedGross - linkedFees;
  const linkedNet = linkedSettlementsNet;
  const paypalSummary = payment.paypalSummary;
  const revolutSummary = payment.revolutSummary;
  const braintreeSummary = payment.braintreeSummary;
  const txSummary = paypalSummary || revolutSummary || braintreeSummary;

  const linkedSum   = hasFeesData ? linkedNet : linkedGross;
  // Revolut carryover folds the chain boundary into the per-payment calc so
  // chained payouts (e.g. 296 -> 294) close cleanly even though neither slice
  // matches its own payout amount alone. revolutSum = sales_net + refunds_net
  // + carryover_in - carryover_out.
  const carryoverIn = isRevolut && payment.revolutCarryoverIn !== null ? payment.revolutCarryoverIn : 0;
  const carryoverOut = isRevolut && payment.revolutCarryoverOut !== null ? payment.revolutCarryoverOut : 0;
  const hasCarryover = isRevolut && (carryoverIn !== 0 || carryoverOut !== 0);
  // For Revolut: keep the UI status in lockstep with the server's
  // calcLinkedSummary by computing the comparison from payment_order_links
  // (the canonical "this order belongs to this payment" record). Settlements
  // that landed in revolut_transactions but never produced a link (e.g. the
  // order isn't in our DB, currency mismatch, or it's already linked to a
  // different payment) surface as a balance drift just like on the server.
  // Refund-funding transfers (Revolut topping up the balance to cover a
  // refund whose sale lived in another payout) are folded in too so the
  // payout balances cleanly.
  // For PayPal we keep totalNet because the PayPal flow always creates a link
  // for every persisted transaction.
  const refundFundingNet = isRevolut && revolutSummary ? (revolutSummary.refundFundingNet || 0) : 0;
  // The PayPal payout arrived net of an intermediary transfer fee; lower the
  // reconciled total by it so the UI matches the server's calcLinkedSummary and
  // reads reconciled instead of underpaid.
  const settlementFee = isPaypal ? (payment.settlementFee || 0) : 0;
  // Vasco invoice links (SW-2486): effective amount is the claimed portion when
  // set, else the full invoice amount. Credit notes carry negative amounts and
  // net out naturally. Guard for test fixtures / pre-migration payloads.
  const vascoLinks = payment.vascoLinks || [];
  const vascoLinkedNet = vascoLinks.reduce(
    (acc, l) =>
      acc + (l.amount != null
        ? parseFloat(l.amount)
        : l.invoiceAmount != null ? parseFloat(l.invoiceAmount) : 0),
    0,
  );
  // Force-closed unlinked settlements (SW-2489) count toward the reconciled
  // sum with their statement net — mirrors fetchForcedSettlementsNet.
  const forcedSettlementsNet =
    (revolutSummary?.unlinkedSettlements || [])
      .filter((u) => u.forceClosedAt)
      .reduce((acc, u) => acc + (u.settlementAmount ?? u.revolutGross), 0)
    + (braintreeSummary?.unlinkedSettlements || [])
      .filter((u) => u.forceClosedAt)
      .reduce((acc, u) => acc + (u.net ?? u.braintreeGross), 0);
  // Unlinked settlements still open (not force-closed): money the payout paid
  // out that no order link vouches for yet. The tie-out line uses it so the
  // full statement total always explains the bank amount:
  // statement total = expected + open items.
  const openUnlinkedNet =
    (revolutSummary?.unlinkedSettlements || [])
      .filter((u) => !u.forceClosedAt)
      .reduce((acc, u) => acc + (u.settlementAmount ?? u.revolutGross), 0)
    + (braintreeSummary?.unlinkedSettlements || [])
      .filter((u) => !u.forceClosedAt)
      .reduce((acc, u) => acc + (u.net ?? u.braintreeGross), 0);
  // For Braintree, the comparison mirrors Revolut's link-based math: AIB's
  // net (sales − refunds) IS the bank credit amount, so we compute from
  // linked sales minus refunds. Order_total mismatches surface as a delta the
  // same way as Revolut. Vasco links are added once, outside the ternary, so
  // they count in every branch — mirroring calcLinkedSummary on the server.
  // Standalone AIB adjustments (chargeback debits and the flat chargeback
  // handling fee). AIB deducts them from the payout, so they lower the expected
  // total exactly like a refund. Mirrors `adjustmentsNet` in calcLinkedSummary.
  const braintreeFeesNet = isBraintree && braintreeSummary ? (braintreeSummary.feesNet || 0) : 0;
  const comparisonSumBeforeCredits = (isRevolut && txSummary
    ? linkedSettlementsNet + (txSummary.refundsNet || 0) + refundFundingNet + carryoverIn - carryoverOut
    : isBraintree && braintreeSummary
    ? (hasFeesData ? linkedNet : linkedGross) + (braintreeSummary.refundsNet || 0) + braintreeFeesNet
    : txSummary
    ? txSummary.totalNet + carryoverIn - carryoverOut - settlementFee
    : (hasFeesData ? linkedNet : linkedGross)) + vascoLinkedNet + forcedSettlementsNet;
  // Vasco credit notes (dobropis) carry negative amounts. A credit note either
  // explains a shortfall (dealer paid the reduced amount) or is a separate
  // later refund (dealer paid in full). Reconcile against BOTH the full and the
  // credit-adjusted total and keep whichever the payment is closer to — so a
  // credit can close an underpayment but never manufacture an overpayment for
  // an order paid in full. Mirrors calcLinkedSummary on the server. See SW-2406.
  const creditNotesNet = activeLinkedOrders.reduce(
    (acc, o) => acc + (o.creditNotes || []).reduce((s, cn) => s + cn.amount, 0),
    0,
  );
  const amountTolerance = isBraintree ? BRAINTREE_AMOUNT_TOLERANCE : isRevolut ? 0.02 : 0.01;
  const remainingFull   = payment.amount - comparisonSumBeforeCredits;
  const remainingCredit = payment.amount - (comparisonSumBeforeCredits + creditNotesNet);
  const creditApplied   = creditNotesNet !== 0 && Math.abs(remainingCredit) < Math.abs(remainingFull);
  const comparisonSum = creditApplied ? comparisonSumBeforeCredits + creditNotesNet : comparisonSumBeforeCredits;
  const remaining    = payment.amount - comparisonSum;
  const isExact      = Math.abs(remaining) <= amountTolerance;
  const isOverpaid   = remaining > amountTolerance;   // payment > orders: dealer paid too much
  const isUnderpaid  = remaining < -amountTolerance;  // payment < orders: dealer paid too little

  return {
    hasFeesData,
    isRevolut,
    isBraintree,
    isPaypal,
    linkedGross,
    linkedFees,
    linkedSettlementsNet,
    linkedNet,
    linkedSum,
    carryoverIn,
    carryoverOut,
    hasCarryover,
    refundFundingNet,
    settlementFee,
    vascoLinkedNet,
    forcedSettlementsNet,
    openUnlinkedNet,
    braintreeFeesNet,
    comparisonSumBeforeCredits,
    creditNotesNet,
    amountTolerance,
    creditApplied,
    comparisonSum,
    remaining,
    isExact,
    isOverpaid,
    isUnderpaid,
  };
}
